import React, { useCallback, useState } from 'react';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import getValidationErrors, {
  IErrors,
} from '../../../../utils/getValidationErrors';
import { NumberFormatCustom } from '../../../../components/NumberFormatCustom';
import { useCreateService } from '../../../../hooks/data/services';
import { IService } from '../../../../shared/interfaces/service-interfaces';
import LoadingButton from '../../../../components/LoadingButton';

type IProps = {
  open: boolean;
  handleClose: () => void;
};

const ServiceForm: React.FC<IProps> = ({ open, handleClose }) => {
  const [data, setData] = useState<Omit<IService, 'id'>>({
    service_name: '',
    time_box: 0,
    price: '',
  });

  const [errors, setErrors] = useState<IErrors>({
    service_name: '',
    time_box: '',
    value: '',
  });

  const createService = useCreateService({});

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setData({
      ...data,
      [e.target.name]: e.target.value,
    });
    setErrors({
      ...errors,
      [e.target.name]: '',
    });
  };

  const handleSubmit = useCallback(async () => {
    try {
      const schema = Yup.object().shape({
        service_name: Yup.string().required('Nome é obrigatório'),
        time_box: Yup.number().min(1, 'Duração deve ser maior que 1'),
        price: Yup.string(),
      });

      await schema.validate(data, {
        abortEarly: false,
      });

      await createService.mutateAsync(data);

      setData({
        service_name: '',
        time_box: 0,
        price: '',
      });
      setErrors({});
      handleClose();
      toast.success('Serviço cadastrado com sucesso');
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const validationErrors = getValidationErrors(err);

        setErrors(validationErrors);

        return;
      }

      toast.error('Erro no cadastro do serviço');
    }
  }, [createService, data, handleClose]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="form-dialog-title"
    >
      <DialogTitle id="form-dialog-title">Cadastro de serviço</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          id="service_name"
          name="service_name"
          label="Nome"
          type="text"
          fullWidth
          variant="outlined"
          value={data.service_name}
          onChange={handleChange}
          helperText={errors.name}
          error={!!errors.name}
        />
        <TextField
          margin="dense"
          id="time_box"
          name="time_box"
          label="Duração (em min)"
          type="number"
          fullWidth
          variant="outlined"
          value={data.time_box > 0 ? data.time_box : ''}
          onChange={handleChange}
          helperText={errors.time_box}
          error={!!errors.time_box}
        />
        <TextField
          margin="dense"
          id="formatted-numberformat-input"
          label="Valor do serviço"
          fullWidth
          variant="outlined"
          name="price"
          value={data.price}
          onChange={handleChange}
          InputProps={{
            inputComponent: NumberFormatCustom as any,
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleClose}
          color="primary"
          disabled={createService.isLoading}
        >
          Cancelar
        </Button>
        <LoadingButton
          loading={createService.isLoading}
          onClick={handleSubmit}
          color="primary"
          variant="contained"
        >
          Salvar
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export { ServiceForm };
